import { claimflowApiClient } from './claimflowApiClient';

/**
 * Documents component API service.
 *
 * Every call goes through origami's generic ERP proxy
 * (`/claimflow/erp/registries/veos/endpoints/{slug}`), which wraps the
 * `ErpProxyResponse<T>` envelope around the raw VEOS payload. GET inputs are
 * passed as query params; POST inputs go in the request body under `data`
 * (the proxy merges `query()` + `input('data')` before routing to VEOS).
 *
 * See `.claude-work/specs/claimflow-documents-component.md` (mahhp 21783) for
 * the full contract, slugs, and response shapes.
 */

interface ErpProxyResponse<T> {
  success: boolean;
  http_status?: number;
  data: T;
  error?: { code: string; message: string } | null;
}

type ErpListResponse<T> = ErpProxyResponse<{
  total?: number;
  // VEOS may omit `data` on an empty result — keep it optional so the
  // `?? []` fallback at the call sites is type-sound.
  data?: T[] | null;
}>;

/**
 * A row from `WS2_GS_TAB_DOCUMENTS`. `API` selects which content endpoint to
 * use: `"DOC"` → `document-get`, `"EMAIL"` → `mail-get`. A `"DOC"` row whose
 * `CONTENT_TYPE` is an Outlook/`message/rfc822` type is a *received* email.
 */
export interface VeosDocumentRow {
  API: string;
  ID_ADOC: number;
  LIB_ADOC: string;
  CONTENT_TYPE: string;
  DAT_ADOC: string;
  TYPE_ADOC: string;
  LIB_DS: string | null;
  ACATEGORISER: number;
  /** File name. VEOS returns `null` for some rows, so guard before using it. */
  NOM_ADOC: string | null;
  /**
   * Name of the user who filed the document (the "Créateur" column). Added to
   * `WS2_GS_TAB_DOCUMENTS` for mahhp 22268. Optional/nullable: older payloads
   * (and rows filed before the VEOS change) omit it. NB the ticket also writes
   * this as `NOM_PER_SER` in some examples — {@link getDocumentCreator} reads
   * either spelling defensively.
   */
  NOM_PER_USER?: string | null;
  /**
   * Document direction: `"ENTRANT"` (received) or `"SORTANT"` (sent). Added for
   * mahhp 22268 (the "Sens" column). Optional/nullable — absent on older rows.
   */
  SENS?: string | null;
  /**
   * `1` when this document is attached to the current task (the one that gave
   * rise to it) — surfaced as the star + "Nouveau" marker in the "Statut"
   * column. `0`/absent otherwise. Only populated when the list is fetched with
   * an `id_event` (the current task id). Added for mahhp 22268.
   */
  DOC_TACHE?: number | null;
}

/**
 * The full content object for an `API="DOC"` document. `datas` is the base64
 * of the file. Extra replay fields are kept loose — we only read a handful.
 */
export interface VeosDocumentContent {
  id: number | string;
  contentType?: string;
  creationDate?: string;
  label?: string;
  name?: string;
  type?: string;
  tab?: string;
  service?: string;
  idTab?: number | string;
  datas: string;
  classement?: string;
  sortant?: unknown;
  [key: string]: unknown;
}

export interface VeosMailRecipient {
  id?: number | string;
  mail?: string;
  label?: string;
}

export interface VeosMailSender {
  mail?: string;
  nom?: string;
}

export interface VeosMailAttachment {
  id: number | string;
  name: string;
  contentType: string;
  data: string | null;
  cleanContentType?: string;
  /** Byte size, when VEOS provides it on the listing (else computed lazily). */
  size?: number | null;
}

/** Response of `mail-get` (a sent email). */
export interface VeosMail {
  id: number | string;
  dateEnvoi?: string;
  envoiProg?: string;
  sujet?: string;
  corps?: string;
  cdServ?: string;
  idtab?: number | string;
  confid?: string;
  mailType?: string;
  dtcre?: string;
  dest?: VeosMailRecipient[];
  cc?: VeosMailRecipient[];
  cci?: VeosMailRecipient[];
  exp?: VeosMailSender;
  pjs?: VeosMailAttachment[];
  important?: unknown;
}

/** A single fetched sent-email attachment (`mail-pj-get`). */
export interface VeosMailAttachmentContent {
  id: number | string;
  name: string;
  contentType: string;
  data: string;
  cleanContentType?: string;
  size?: number | null;
}

/** A "Classement" category option from `WS2_GS_SEL_CDDS`. */
export interface VeosClassementOption {
  CD_DS: string;
  LIB_DS: string;
}

/**
 * Which GED a documents query targets (`tab_adoc`, mahhp 22227): the claim's
 * documents (`SINISTRE` — VEOS' default when the param is absent) or the
 * contract's (`POLICE`, the "Prod" side of the Documents header toggle).
 */
export type DocumentsTabAdoc = 'SINISTRE' | 'POLICE';

/**
 * Optional server-side filters for {@link documentsService.fetchDocuments}
 * (mahhp 22227). Each one is forwarded only when set, so an absent / empty
 * filter leaves the request exactly as before.
 */
export interface DocumentsListFilters {
  /** Category code (`CD_DS`) → `cd_ds`. Empty / absent = every category. */
  cdDs?: string;
  /** GED scope → `tab_adoc`. Absent = VEOS' default (the claim's documents). */
  tabAdoc?: DocumentsTabAdoc;
}

/** A raw row of `WS2_GS_SEL_CDDS_GED` (the categories present in a GED). */
interface VeosGedCategoryRow {
  CD_DS: string | number | null;
  LIB_DS: string | null;
}

/**
 * A Documents category-filter option (`WS2_GS_SEL_CDDS_GED`, mahhp 22227):
 * the category code sent back as `cd_ds` + its display label.
 */
export interface GedCategoryOption {
  code: string;
  label: string;
}

/**
 * Map `WS2_GS_SEL_CDDS_GED` rows to typed options. Rows without a code are
 * dropped (they cannot be expressed as a `cd_ds` filter) and a repeated code
 * keeps its first occurrence, so the options are safe to key by `code`.
 */
function toGedCategoryOptions(rows: readonly VeosGedCategoryRow[]): GedCategoryOption[] {
  const seen = new Set<string>();
  const options: GedCategoryOption[] = [];
  for (const row of rows) {
    const code = row.CD_DS == null ? '' : String(row.CD_DS).trim();
    if (code === '' || seen.has(code)) continue;
    seen.add(code);
    options.push({ code, label: (row.LIB_DS ?? '').trim() || code });
  }
  return options;
}

export interface UpdateGedInput {
  TYPE: string;
  ID: string;
  CD_DS: string;
  LIB: string;
}

export interface PutAttachmentInGedInput {
  contentType: string;
  datas: string;
  idTab: string;
  label: string;
  name: string;
  type: string;
  /** dd/mm/yyyy — defaults to today when omitted. */
  creationDate?: string;
}

function unwrap<T>(envelope: ErpProxyResponse<T>): T {
  if (!envelope.success) {
    throw new Error(envelope.error?.message ?? 'ERP request failed');
  }
  return envelope.data;
}

function todayDdMmYyyy(): string {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = String(now.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

export const documentsService = {
  /**
   * List a claim's documents (`WS2_GS_TAB_DOCUMENTS`).
   *
   * `idEvent` (the current task's `ID_EVENT`) is forwarded as `id_event` when
   * provided: VEOS uses it to set each row's `DOC_TACHE` flag (which documents
   * are attached to the task). Omitted ⇒ the flag comes back unset (mahhp 22268).
   *
   * `filters` (mahhp 22227) narrows the list server-side: `cdDs` → `cd_ds` (one
   * category) and `tabAdoc` → `tab_adoc` (the claim's or the contract's GED).
   * Each param is sent only when set, so omitting them keeps the exact request
   * made before.
   */
  async fetchDocuments(
    idSin: string | number,
    idEvent?: string | number,
    filters?: DocumentsListFilters,
  ): Promise<VeosDocumentRow[]> {
    const response = await claimflowApiClient.get<ErpListResponse<VeosDocumentRow>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-tab-documents',
      // `perPage: 9999` overrides VEOS's default pagination (perPage=15), which
      // otherwise truncates the documents list. 9999 is the codebase convention.
      {
        params: {
          id_sin: idSin,
          perPage: 9999,
          ...(idEvent != null && idEvent !== '' ? { id_event: idEvent } : {}),
          ...(filters?.cdDs ? { cd_ds: filters.cdDs } : {}),
          ...(filters?.tabAdoc ? { tab_adoc: filters.tabAdoc } : {}),
        },
      },
    );
    return unwrap(response.data).data ?? [];
  },

  /**
   * Categories present in a claim's GED (`WS2_GS_SEL_CDDS_GED`, mahhp 22227) —
   * the options of the Documents header category filter. `tabAdoc` scopes them
   * to the claim's (`SINISTRE`) or the contract's (`POLICE`) documents; it is
   * sent only when set. Not to be confused with {@link fetchClassementOptions}
   * (`WS2_GS_SEL_CDDS`, every category a document can be filed under).
   */
  async fetchGedCategories(
    idSin: string | number,
    tabAdoc?: DocumentsTabAdoc,
  ): Promise<GedCategoryOption[]> {
    const response = await claimflowApiClient.get<ErpListResponse<VeosGedCategoryRow>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-cdds-ged',
      {
        params: {
          id_sin: idSin,
          perPage: 9999,
          ...(tabAdoc ? { tab_adoc: tabAdoc } : {}),
        },
      },
    );
    return toGedCategoryOptions(unwrap(response.data).data ?? []);
  },

  /** Fetch a document's content (`document-get`) — `API="DOC"` only. */
  async fetchDocumentContent(id: string | number): Promise<VeosDocumentContent> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosDocumentContent>>(
      '/claimflow/erp/registries/veos/endpoints/document-get',
      { params: { id } },
    );
    return unwrap(response.data);
  },

  /** Fetch a sent email (`mail-get`) — `API="EMAIL"` only. */
  async fetchSentEmail(id: string | number): Promise<VeosMail> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosMail>>(
      '/claimflow/erp/registries/veos/endpoints/mail-get',
      { params: { id } },
    );
    return unwrap(response.data);
  },

  /** Fetch a sent-email attachment's content (`mail-pj-get`). */
  async fetchSentEmailAttachment(
    mailId: string | number,
    pjId: string | number,
  ): Promise<VeosMailAttachmentContent> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosMailAttachmentContent>>(
      '/claimflow/erp/registries/veos/endpoints/mail-pj-get',
      { params: { mailId, pjId } },
    );
    return unwrap(response.data);
  },

  /** Categorize / re-label an existing document (`EXT_MODIF_GED`). */
  async updateGed(input: UpdateGedInput): Promise<void> {
    const response = await claimflowApiClient.post<ErpProxyResponse<unknown>>(
      '/claimflow/erp/registries/veos/endpoints/boby-json-modif-ged',
      {
        data: {
          TYPE: input.TYPE,
          ID: input.ID,
          CD_DS: input.CD_DS,
          LIB: input.LIB,
        },
      },
    );
    unwrap(response.data);
  },

  /** Put a (received-email) attachment into GED (`document-create`). */
  async putAttachmentInGed(input: PutAttachmentInGedInput): Promise<void> {
    const response = await claimflowApiClient.post<ErpProxyResponse<unknown>>(
      '/claimflow/erp/registries/veos/endpoints/document-create',
      {
        data: {
          confid: '0',
          contentType: input.contentType,
          creationDate: input.creationDate ?? todayDdMmYyyy(),
          datas: input.datas,
          idTab: input.idTab,
          label: input.label,
          name: input.name,
          para: '',
          service: 'SINISTRE',
          tab: 'SINISTRE',
          color: '',
          modifDate: '',
          sign: '',
          user: '',
          type: input.type,
        },
      },
    );
    unwrap(response.data);
  },

  /** "Classement" category options (`WS2_GS_SEL_CDDS`). */
  async fetchClassementOptions(): Promise<VeosClassementOption[]> {
    const response = await claimflowApiClient.get<ErpListResponse<VeosClassementOption>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-cdds',
      { params: { perPage: 9999 } },
    );
    return unwrap(response.data).data ?? [];
  },
};
